<template>
  <div>

    <!-- ===== PAGE HEADER (azione) ===== -->
    <div class="d-flex align-items-center justify-content-end flex-wrap mb-4" style="gap:12px;">
      <button
        v-if="mode === 'read' && selectedCommessa"
        class="au-btn-submit"
        @click="setAddMode"
      >
        <i class="mdi mdi-plus mr-1"></i> Aggiungi Nuovo
      </button>
      <button v-else-if="mode === 'add' || mode === 'edit'" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">
        <i class="mdi mdi-arrow-left mr-1"></i> Torna all'elenco
      </button>
    </div>

    <!-- Commessa e Modulo selector -->
    <div class="au-card mb-4" v-if="mode === 'read'">
      <div class="row align-items-end" style="gap: 12px 0;">
        <div class="col-md-6 text-left">
          <label class="cw-label mb-2">Commessa</label>
          <div class="cw-select-wrap">
            <i class="mdi mdi-briefcase-outline cw-field-icon"></i>
            <select v-model="selectedCommessa" class="cw-select">
              <option value="" disabled>Seleziona una commessa...</option>
              <option v-for="c in commesse" :key="c.id" :value="String(c.id)">{{ c.nome }}</option>
            </select>
            <i class="mdi mdi-chevron-down cw-select-arrow"></i>
          </div>
        </div>
        <div class="col-md-6 text-left" v-if="selectedCommessa">
          <label class="cw-label mb-2">Filtra per Modulo</label>
          <div class="cw-select-wrap">
            <i class="mdi mdi-layers-outline cw-field-icon"></i>
            <select v-model="selectedModuleFilter" class="cw-select">
              <option value="">Tutti i moduli</option>
              <option v-for="m in modules" :key="m.id" :value="String(m.id)">{{ m.nome }}</option>
            </select>
            <i class="mdi mdi-chevron-down cw-select-arrow"></i>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!selectedCommessa && mode === 'read'" class="text-center py-5">
      <i class="mdi mdi-briefcase-off-outline text-muted mb-3 d-block" style="font-size: 3rem; opacity: 0.5;"></i>
      <h5 class="text-muted">Seleziona una commessa per visualizzare gli asset.</h5>
    </div>

    <!-- LIST MODE (B/A) -->
    <div class="au-card" v-if="mode === 'read' && selectedCommessa">
      <div class="cw-section-title">Asset della Commessa</div>
      <div class="table-responsive">
        <table class="au-table">
          <thead>
            <tr>
              <th style="width: 70px;">ID</th>
              <th>Identificativo</th>
              <th>Tipo Asset</th>
              <th>Modulo</th>
              <th>Ubicazione / Indirizzo</th>
              <th class="text-right" style="width: 190px;">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="6" class="text-center py-4">
                <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                Caricamento asset...
              </td>
            </tr>
            <tr v-else-if="filteredAssets.length === 0">
              <td colspan="6" class="text-center py-5 text-muted">
                <i class="mdi mdi-cube-off-outline d-block mb-2" style="font-size: 2.4rem; opacity:0.3;"></i>
                <span class="font-weight-semibold d-block text-dark">Nessun asset presente</span>
                Non ci sono asset associati per il modulo o commessa selezionati.
              </td>
            </tr>
            <tr v-for="item in filteredAssets" :key="item.id" v-else>
              <td class="font-weight-bold text-muted">#{{ item.id }}</td>
              <td><span class="font-weight-bold text-dark">{{ item.identificativo }}</span></td>
              <td>
                <span class="au-company-badge text-primary" style="background:#FEF1E0;">
                  {{ item.asset_type_nome }}
                </span>
              </td>
              <td class="text-muted">{{ item.module_nome || '—' }}</td>
              <td>
                <span class="text-muted small">
                  {{ getAssetLocation(item) }}
                </span>
              </td>
              <td class="text-right text-nowrap">
                <button
                  class="au-btn-action mr-1"
                  @click="editAsset(item)"
                  v-tooltip="'Modifica'"
                >
                  <i class="mdi mdi-pencil-outline mr-1"></i> Modifica
                </button>
                <button
                  class="btn btn-sm btn-outline-danger rounded-pill px-3 font-weight-semibold"
                  @click="deleteAsset(item)"
                >
                  Elimina
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ADD / EDIT FORM MODE (B/A) -->
    <div class="row justify-content-center" v-if="mode === 'add' || mode === 'edit'">
      <div class="col-lg-8 col-md-10 mb-4">
        <div class="au-card h-100 text-left">

          <div class="edit-mode-banner mb-4" v-if="mode === 'edit'">
            <i class="mdi mdi-pencil-circle-outline mr-2" style="font-size:1.2rem;"></i>
            Stai modificando l'asset <strong>{{ formAsset.identificativo }}</strong>
          </div>

          <div class="cw-section-title">
            <span class="cw-step-num">1</span>Tipo di Asset
          </div>

          <div class="row">
            <div class="col-md-6 form-group mb-4 text-left">
              <label class="cw-label">Modulo <span class="text-danger">*</span></label>
              <div class="cw-select-wrap">
                <i class="mdi mdi-layers-outline cw-field-icon"></i>
                <select
                  v-model="formAsset.fk_module"
                  class="cw-select"
                  :class="{ 'cw-disabled': mode === 'edit' }"
                  :disabled="mode === 'edit'"
                  @change="fetchAssetTypesForModule"
                >
                  <option value="" disabled>Seleziona modulo...</option>
                  <option v-for="mod in modules" :key="mod.id" :value="mod.id">{{ mod.nome }}</option>
                </select>
                <i class="mdi mdi-chevron-down cw-select-arrow"></i>
              </div>
            </div>
            <div class="col-md-6 form-group mb-4 text-left">
              <label class="cw-label">Tipo Asset <span class="text-danger">*</span></label>
              <div class="cw-select-wrap">
                <i class="mdi mdi-cog-outline cw-field-icon"></i>
                <select
                  v-model="formAsset.fk_asset_type"
                  class="cw-select"
                  :class="{ 'cw-disabled': mode === 'edit' || !formAsset.fk_module }"
                  :disabled="mode === 'edit' || !formAsset.fk_module"
                  @change="onAssetTypeSelected"
                >
                  <option value="" disabled>Seleziona tipo...</option>
                  <option v-for="t in availableAssetTypes" :key="t.id" :value="t.id">{{ t.nome }}</option>
                </select>
                <i class="mdi mdi-chevron-down cw-select-arrow"></i>
              </div>
              <small class="text-muted d-block mt-1" style="font-size:0.72rem;" v-if="mode === 'edit'">
                Il tipo di asset non è modificabile dopo la creazione.
              </small>
            </div>
          </div>

          <form @submit.prevent="mode === 'edit' ? updateAsset() : saveAsset()">
            <div class="cw-section-title mt-2">
              <span class="cw-step-num">2</span>Dati Asset
            </div>

            <div class="form-group mb-4 text-left">
              <label class="cw-label">Identificativo <span class="text-danger">*</span></label>
              <div class="cw-input-wrap">
                <i class="mdi mdi-identifier cw-field-icon"></i>
                <input v-model="formAsset.identificativo" class="cw-input" placeholder="Es. LAMP-001" required>
              </div>
            </div>

            <!-- Dynamic fields from asset type schema -->
            <div v-if="dynamicFields.length > 0" class="row">
              <div
                v-for="field in dynamicFields"
                :key="field.name"
                class="col-md-6 form-group mb-4 text-left"
              >
                <label class="cw-label">
                  {{ field.label || field.name }}
                  <span class="text-danger" v-if="field.required">*</span>
                </label>
                <div class="cw-input-wrap">
                  <i class="mdi mdi-form-textbox cw-field-icon"></i>
                  <input
                    v-model="formAsset.dati[field.name]"
                    :type="field.type === 'number' ? 'number' : (field.type === 'date' ? 'date' : 'text')"
                    class="cw-input"
                    :placeholder="field.label || field.name"
                    :required="!!field.required"
                  >
                </div>
              </div>
            </div>

            <div class="dati-empty-notice mb-4" v-else-if="formAsset.fk_asset_type">
              <i class="mdi mdi-information-outline mr-2"></i>
              Questo tipo di asset non prevede campi aggiuntivi.
            </div>

            <div class="cw-footer mt-2 d-flex align-items-center justify-content-between flex-wrap">
              <small class="text-muted">
                <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem;"></i> Campi obbligatori
              </small>
              <div class="d-flex" style="gap:12px;">
                <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">Annulla</button>
                <button type="submit" class="cw-btn-submit" :disabled="submitting || !formAsset.fk_asset_type">
                  <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                  <i class="mdi mdi-content-save-outline mr-1" v-else></i>
                  {{ mode === 'edit' ? 'Aggiorna Asset' : 'Salva Asset' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { parseCampiSchema, buildCreatePayload, buildUpdatePayload } from "@/utils/assetSchema";
import { contextStore } from "@/utils/contextStore";

export default {
  name: "CompanyAssetsManager",
  props: {
    // companyId dell'azienda corrente. Se non passato, il componente
    // ricade su parseInt(localStorage.getItem("fk_company")), come oggi.
    companyId: { type: Number, default: null }
  },
  data() {
    return {
      mode: "read",
      loading: false,
      submitting: false,

      modules: [],

      assets: [],
      availableAssetTypes: [],
      dynamicFields: [],
      formAsset: {
        id: null,
        fk_module: "",
        fk_asset_type: "",
        identificativo: "",
        dati: {}
      }
    };
  },
  computed: {
    contextStore() {
      return contextStore;
    },
    effectiveCompanyId() {
      return this.companyId || parseInt(localStorage.getItem("fk_company")) || null;
    },
    // Commessa e modulo sono centralizzati nel contextStore (selezione condivisa
    // con la navbar e tutte le altre pagine del backoffice).
    commesse() {
      return contextStore.commesse;
    },
    selectedCommessa: {
      get() {
        return contextStore.activeCommessaId;
      },
      set(val) {
        contextStore.setCommessa(val);
      }
    },
    selectedModuleFilter: {
      get() {
        return contextStore.selectedModuleId;
      },
      set(val) {
        contextStore.setSelectedModule(val);
      }
    },
    filteredAssets() {
      if (!this.selectedModuleFilter) {
        return this.assets;
      }
      return this.assets.filter(item => {
        const modId = item.fk_module || item.module_id;
        return String(modId) === String(this.selectedModuleFilter);
      });
    }
  },
  watch: {
    // Quando la commessa attiva cambia (da qui o dalla navbar), ricarica gli asset.
    "contextStore.activeCommessaId"() {
      this.fetchAssets();
    }
  },
  async mounted() {
    this.fetchModules();
    await contextStore.fetchCommesse();
    if (this.selectedCommessa) {
      this.fetchAssets();
    }
  },
  methods: {
    getAssetLocation(item) {
      if (!item) return '—';
      if (item.ubicazione) return item.ubicazione;
      if (item.indirizzo) return item.indirizzo;
      if (item.via) return item.via;

      if (item.dati && typeof item.dati === 'object') {
        const keys = Object.keys(item.dati);
        const locationKey = keys.find(k => {
          const lower = k.toLowerCase();
          return lower.includes('ubicazione') || lower.includes('indirizzo') || lower.includes('via') || lower.includes('localita') || lower.includes('posizione') || lower.includes('strada') || lower.includes('comune');
        });
        if (locationKey && item.dati[locationKey]) {
          return item.dati[locationKey];
        }
        if (item.dati.latitude && item.dati.longitude) {
          return `${item.dati.latitude}, ${item.dati.longitude}`;
        }
        if (item.dati.lat && item.dati.lng) {
          return `${item.dati.lat}, ${item.dati.lng}`;
        }
      }
      return '—';
    },
    fetchModules() {
      const url = `/companies/${this.effectiveCompanyId}/modules`;
      this.axios.get(url).then((response) => {
        this.modules = Array.isArray(response.data) ? response.data : [];
      }).catch(console.error);
    },
    fetchAssets() {
      if (!this.selectedCommessa) return;
      this.loading = true;
      this.axios.get(`/commesse/${this.selectedCommessa}/assets`)
        .then(res => {
          this.assets = (res.data && res.data.data) ? res.data.data : [];
        })
        .catch(console.error)
        .finally(() => { this.loading = false; });
    },
    fetchAssetTypesForModule() {
      this.formAsset.fk_asset_type = "";
      this.dynamicFields = [];
      this.formAsset.dati = {};
      if (!this.formAsset.fk_module) return;

      const params = {};
      if (this.selectedCommessa) {
        params.commessa_id = this.selectedCommessa;
      }

      this.axios.get(`/modules/${this.formAsset.fk_module}/asset_types`, { params })
        .then(res => {
          const types = (res.data && res.data.data) ? res.data.data : [];
          this.availableAssetTypes = types.map(t => ({
            ...t,
            campi_schema: parseCampiSchema(t.campi_schema)
          }));
        }).catch(console.error);
    },
    onAssetTypeSelected() {
      const selectedType = this.availableAssetTypes.find(t => t.id === this.formAsset.fk_asset_type);
      this.dynamicFields = (selectedType) ? parseCampiSchema(selectedType.campi_schema) : [];
      // Inizializza le chiavi dello schema in modo reattivo (Vue 2)
      const dati = {};
      this.dynamicFields.forEach(f => { dati[f.name] = ""; });
      this.formAsset.dati = dati;
    },
    setAddMode() {
      this.mode = "add";
      this.formAsset.id = null;
      this.formAsset.fk_module = "";
      this.formAsset.fk_asset_type = "";
      this.formAsset.identificativo = "";
      this.formAsset.dati = {};
      this.availableAssetTypes = [];
      this.dynamicFields = [];
    },
    saveAsset() {
      this.submitting = true;
      this.axios.post('/assets', buildCreatePayload({
        fkCompany: this.effectiveCompanyId,
        fkCommessa: this.selectedCommessa,
        fkAssetType: this.formAsset.fk_asset_type,
        identificativo: this.formAsset.identificativo,
        dati: this.formAsset.dati
      })).then(res => {
        if (res.data && res.data.success) {
          this.$swal.fire("Salvato", "Asset inserito con successo", "success");
          this.mode = "read";
          this.fetchAssets();
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare", "error");
        }
      }).catch(console.error).finally(() => { this.submitting = false; });
    },
    editAsset(item) {
      // Precompila TUTTI i campi dell'asset (identificativo + campi dinamici)
      this.formAsset.fk_module = item.fk_module;
      this.formAsset.identificativo = item.identificativo;

      const params = {};
      if (this.selectedCommessa) {
        params.commessa_id = this.selectedCommessa;
      }

      // Carica le tipologie del modulo, poi imposta tipo + campi dinamici + dati
      this.axios.get(`/modules/${item.fk_module}/asset_types`, { params })
        .then(res => {
          const types = (res.data && res.data.data) ? res.data.data : [];
          this.availableAssetTypes = types.map(t => ({
            ...t,
            campi_schema: parseCampiSchema(t.campi_schema)
          }));

          this.formAsset.fk_asset_type = item.fk_asset_type;
          const selectedType = this.availableAssetTypes.find(t => t.id === item.fk_asset_type);
          this.dynamicFields = selectedType ? parseCampiSchema(selectedType.campi_schema) : [];

          // Ricostruisci i dati assicurando la reattività su tutte le chiavi dello schema
          const existing = item.dati || {};
          const dati = {};
          this.dynamicFields.forEach(f => { dati[f.name] = existing[f.name] !== undefined ? existing[f.name] : ""; });
          // Conserva anche eventuali chiavi presenti nei dati ma non più nello schema
          Object.keys(existing).forEach(k => { if (dati[k] === undefined) dati[k] = existing[k]; });
          this.formAsset.dati = dati;

          this.formAsset.id = item.id;
          this.mode = "edit";
        })
        .catch(err => {
          console.error(err);
          this.$swal.fire("Errore", "Impossibile caricare i tipi di asset.", "error");
        });
    },
    updateAsset() {
      if (!this.formAsset.identificativo) {
        this.$swal.fire("Attenzione", "L'identificativo è obbligatorio.", "warning");
        return;
      }
      this.submitting = true;
      this.axios.put(`/assets/${this.formAsset.id}`, buildUpdatePayload({
        fkCompany: this.effectiveCompanyId,
        identificativo: this.formAsset.identificativo,
        dati: this.formAsset.dati
      })).then(res => {
        if (res.data && res.data.success) {
          this.$swal.fire("Aggiornato", "Asset aggiornato con successo", "success");
          this.mode = "read";
          this.fetchAssets();
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile aggiornare", "error");
        }
      }).catch(err => {
        console.error(err);
        this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
      }).finally(() => { this.submitting = false; });
    },
    deleteAsset(item) {
      this.$swal.fire({
        title: "Elimina Asset?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sì, elimina",
        cancelButtonText: "Annulla"
      }).then((result) => {
        if (result.value) {
          this.axios.delete(`/assets/${item.id}`)
            .then(res => {
              if (res.data && res.data.success) {
                this.$swal.fire("Eliminato!", "Asset rimosso", "success");
                this.fetchAssets();
              } else {
                this.$swal.fire("Errore", res.data.error || "Impossibile eliminare", "error");
              }
            });
        }
      });
    }
  }
};
</script>

<style scoped>
/* =========================================
   COMPANY ASSETS MANAGER — Premium UI (RoadME DS)
   Stili estratti dalla parte azienda di AdminAssets.vue
   ========================================= */

.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}
.cw-label {
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: #94a3b8; margin-bottom: 8px; display: block;
}
.cw-input-wrap { position: relative; display: flex; align-items: center; }
.cw-field-icon {
  position: absolute; left: 12px;
  color: #94a3b8; font-size: 1rem; z-index: 2; pointer-events: none;
}
.cw-input {
  width: 100%; height: 46px; padding: 0 14px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; font-size: 0.92rem; font-weight: 500; color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-input:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-wrap { position: relative; display: flex; align-items: center; }
.cw-select {
  width: 100%; height: 46px; padding: 0 38px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; appearance: none; -webkit-appearance: none;
  font-size: 0.92rem; font-weight: 500; color: #1e293b;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-select:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-arrow {
  position: absolute; right: 14px;
  color: #94a3b8; font-size: 1rem; pointer-events: none;
}
.cw-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.cw-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.cw-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.cw-footer {
  background: #fff; border-radius: 14px;
  padding: 18px 24px; border: 1px solid rgba(0,0,0,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

.au-card {
  background: #fff; border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  padding: 28px; border: 1px solid rgba(0,0,0,0.05);
}

.au-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.au-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.au-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

.au-table { width: 100%; border-collapse: collapse; }
.au-table thead tr { border-bottom: 2px solid #f1f5f9; }
.au-table thead th {
  padding: 10px 16px; font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8; background: #f8fafc;
}
.au-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.15s; }
.au-table tbody tr:hover { background: #f8fafc; }
.au-table td { padding: 14px 16px; vertical-align: middle; }

.au-company-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
}

.au-btn-action {
  display: inline-flex; align-items: center;
  padding: 6px 16px; border-radius: 20px; border: 1.5px solid #e2e8f0;
  background: #fff; color: #F7931E; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; transition: all 0.15s; text-decoration: none;
}
.au-btn-action:hover {
  background: #FEF1E0; border-color: #F7931E; color: #E07B00; text-decoration: none;
}

.dati-empty-notice {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #166534;
}
.dati-empty-notice .mdi {
  font-size: 1.1rem;
  color: #16a34a;
}

.edit-mode-banner {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #FEF1E0, #FEF1E0);
  border: 1px solid #FBD9AE;
  border-left: 4px solid #F7931E;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #C96A00;
}
.edit-mode-banner .mdi {
  color: #E07B00;
}
</style>
